import type { HotBoardEntry } from '../gen/catalog_pb.js';
import type { HeatCard } from '../live/hot-boards.js';
import { h } from './dom.js';

/** The Heating Up producer owns selection and ordering: BroadcastList.hot_boards. */
export type TopGame = Pick<HotBoardEntry, 'roundId' | 'boardId' | 'whiteName' | 'blackName'>
  & Partial<Pick<HotBoardEntry, 'tourId' | 'eventName' | 'roundName' | 'headline' | 'tension' | 'fen'>> & {
  /** A MATE chip is on the board (the poster's `is_breaking`). */
  breaking?: boolean;
  whiteTitle?: string; blackTitle?: string; whiteFederation?: string; blackFederation?: string;
};
export interface TopGamesSelection { entries: readonly TopGame[]; preview: boolean }

/**
 * The largest swing, in pawns, that is still a pawn count. The heat model clamps a mate to
 * ±`MATE_EQUIV_CP` (1000 cp, ten pawns) and the producer encodes a mate as `10_000 − 10·n` cp
 * (`commentary-producers/src/wordbank.rs::score_to_cp`), so a delta at or past this bound crossed
 * into or out of a mate: "78.0 pawns" is a mate score, not a measurement. Such a swing is named,
 * never counted. Pinned to the Rust constant by `test/band-headline.test.ts`.
 */
export const MAX_COUNTED_SWING_PAWNS = 10; // mirror-of: relay-server-rs/relay-ingest/src/heat/model.rs MATE_EQUIV_CP

/**
 * The Heating Up hook as the web shows it (design review 2026-09-23 finding 9). The ONE owner of
 * the words is the producer, `relay-server-rs/relay-ingest/src/heat/templates.rs`
 * (`MomentFacts::Swing` → "{w}–{b}: the evaluation moved {} pawns"); the backend is left as it
 * is and this only re-words its known templates for a card that already names both players:
 * the "W–B: " pair goes and the clause is said plainly, the number kept. Any other hook (a
 * named-move line, an editor's sentence) is shown as served.
 */
export function bandHeadline(headline: string): string {
  const pair = /^[^:]+–[^:]+: (.+)$/.exec(headline);
  if (pair === null) return headline;
  const clause = pair[1] ?? "";
  const swing = /^the evaluation moved (\d+(?:\.\d+)?) pawns?$/.exec(clause);
  if (swing !== null) {
    const pawns = Number(swing[1]);
    return pawns >= MAX_COUNTED_SWING_PAWNS ? "A decisive swing" : `A ${swing[1]}-pawn swing`;
  }
  const mate = /^the engine sees mate in (\d+) for (White|Black)$/.exec(clause);
  if (mate !== null) return `Mate in ${mate[1]} for ${mate[2]}`;
  if (clause === "the engine sees a forced mate") return "A forced mate on the board";
  return clause.charAt(0).toUpperCase() + clause.slice(1);
}

/**
 * The Heating Up cards for the round page's bottom band, in producer order
 * (`heatCards` decides what each card says). `portrait` supplies the compact
 * player disc — the shared `playerPortrait`, cached by the page so a repaint
 * never reloads a photo.
 */
export function heatingUpCards(cards: readonly HeatCard[], select: (card: HeatCard) => void, portrait: (name: string, roundId: string, boardId: number, white: boolean) => HTMLElement | null): HTMLElement[] {
  return cards.map(card => {
    let tension: HTMLElement | null = null;
    if (card.tension !== null) {
      const fill = h('i');
      // CSSOM, never a style= attribute: the CSP has no 'unsafe-inline' (ui/dom.ts).
      fill.style.setProperty('--tension', `${card.tension}%`);
      tension = h('span', { class: 'heat-tension', role: 'img', 'aria-label': `Tension ${card.tension} of 100` }, fill);
    }
    return h('button', {
      type: 'button', class: 'heat-card', 'data-focus-key': `heat-${card.key}`, 'aria-pressed': String(card.current),
      title: `${card.white.name} vs ${card.black.name}`, onclick: () => select(card),
    },
    card.event || card.round ? h('span', { class: 'heat-section' },
      card.event ? h('span', { class: 'heat-event', text: card.event, title: card.event }) : null,
      card.round ? h('span', { class: 'heat-round', text: card.round }) : null) : null,
    h('span', { class: 'heat-players' },
      heatPlayer(card.white, portrait(card.white.name, card.roundId, card.boardId, true)),
      heatPlayer(card.black, portrait(card.black.name, card.roundId, card.boardId, false))),
    card.headline ? h('span', { class: 'heat-headline', text: bandHeadline(card.headline) }) : null,
    tension);
  });
}

function heatPlayer(player: HeatCard['white'], portrait: HTMLElement | null): HTMLElement {
  return h('span', { class: 'heat-player' }, portrait,
    player.flag === null ? null : h('span', { class: 'heat-flag', 'aria-hidden': 'true', text: player.flag }),
    h('span', { class: 'heat-name', text: player.name }));
}
